import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDown, FileSearch02 } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import React from 'react';
import { useLoaderData } from 'react-router-dom';

import { useAsyncData } from '@myhealth-raccoon/services';

import DoctorIcon from 'src/assets/doctor.svg?react';
import ReferralsIcon from 'src/assets/referral.svg?react';
import RepeatsIcon from 'src/assets/repeats.svg?react';
import ScriptsIcon from 'src/assets/scripts.svg?react';
import { ConfigFlagEnum, SiteBaseModel, siteService } from 'src/services/site';

function TabButton({
    selected,
    disabled,
    onClick,
    children,
    Icon,
}: {
    selected?: boolean;
    disabled?: boolean;
    onClick?(): unknown;
    children?: React.ReactNode;
    Icon?: (props: React.SVGProps<SVGSVGElement>) => React.ReactNode;
}) {
    return (
        <button
            className={classNames(
                'flex translate-y-px flex-row items-center gap-2 rounded-t-md border border-gray-200 px-4 py-2.5 text-sm font-semibold leading-tight disabled:bg-gray-100 disabled:text-gray-400 sm:px-5',
                {
                    'text-primary border-b-transparent bg-white': selected,
                    'bg-gray-50': !selected,
                }
            )}
            onClick={onClick}
            disabled={disabled}
        >
            {Icon && <Icon className="hidden size-4 sm:inline-block" />}
            {children}
        </button>
    );
}

export function BookingTabs({ children }: React.PropsWithChildren) {
    const { site } = useLoaderData() as { site: SiteBaseModel };

    const { data: siteConfig } = useAsyncData(
        {
            service: siteService,
            method: siteService.GetPublicConfig,
        },
        Number(site?.id)
    );

    const automedPath = siteConfig?.find((c) => c.id === ConfigFlagEnum.AutomedUrlPath)?.textValue;
    const scriptsEnabled = siteConfig?.find((c) => c.id === ConfigFlagEnum.EnableAutomedScripts)?.value === 1;
    const referralsEnabled = siteConfig?.find((c) => c.id === ConfigFlagEnum.EnableAutomedReferrals)?.value === 1;

    // repeats/scripts/:automedSiteId/:automedPath
    const scriptsUrl =
        (automedPath &&
            scriptsEnabled &&
            `${window.location.origin}/widget/repeats/scripts/${site.automedSiteId}/${automedPath}/${site.bpLocationId || 1}`) ||
        undefined;
    const referralsUrl =
        (automedPath &&
            referralsEnabled &&
            `${window.location.origin}/widget/repeats/referrals/${site.automedSiteId}/${automedPath}/${site.bpLocationId || 1}`) ||
        undefined;
    const manageBookingUrl = `${window.location.origin}/manage/site/${site.automedSiteId}/${site.bpLocationId}`;

    return (
        <>
            <div className="flex flex-row gap-2 pr-3 sm:gap-2.5">
                <TabButton Icon={DoctorIcon} selected>
                    Book
                </TabButton>
                <TabButton selected={false} onClick={() => window.open(manageBookingUrl)} Icon={FileSearch02}>
                    Manage
                </TabButton>
                {(referralsUrl || scriptsUrl) && (
                    <Menu>
                        <MenuButton
                            className={classNames(
                                'data-[open]:text-primary flex translate-y-px flex-row items-center gap-2 rounded-t-md border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-semibold leading-tight disabled:bg-gray-100 disabled:text-gray-400 data-[open]:bg-white sm:px-6'
                            )}
                        >
                            <RepeatsIcon className="hidden size-4 sm:inline-block" />
                            Repeats
                            <ChevronDown className="size-4" />
                        </MenuButton>
                        <MenuItems
                            transition
                            anchor="bottom start"
                            className="z-50 flex w-48 origin-top-right flex-col rounded border border-gray-200 bg-white py-1 shadow transition-[transform,opacity] duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                        >
                            {scriptsUrl && (
                                <MenuItem>
                                    <a
                                        className="bg-white px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50"
                                        target="_blank"
                                        href={scriptsUrl}
                                    >
                                        <ScriptsIcon className="mr-2 inline-block size-5 text-gray-500" />
                                        Repeat Scripts
                                    </a>
                                </MenuItem>
                            )}
                            {referralsUrl && (
                                <MenuItem>
                                    <a
                                        className="bg-white px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50"
                                        target="_blank"
                                        href={referralsUrl}
                                    >
                                        <ReferralsIcon className="mr-2 inline-block size-5 text-gray-500" />
                                        Repeat Referrals
                                    </a>
                                </MenuItem>
                            )}
                        </MenuItems>
                    </Menu>
                )}
            </div>
            {children}
        </>
    );
}
